<script setup>
import DocumentTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatTime } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, all = false } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    all: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenant.name',
            headerCustom: true,
            enableSorting: false,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Client',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'subject',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Sujet',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.subject,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'meansOfCommunication',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Moyen de communication',
                    width: 0,
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.meansOfCommunication.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'scheduledAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Planifié le',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.scheduledAt,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'minutes',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Durée',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text: formatTime(
                        Math.floor(props.row.original.minutes / 60),
                        String(props.row.original.minutes % 60).padStart(
                            2,
                            '0',
                        ),
                    ),
                }),
        }),
        columnHelper.display({
            id: 'details',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Détails',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            props.row.original.comment
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Commentaire',
                                          content: props.row.original.comment,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  title: 'Voir le commentaire',
                                                  type: 'secondary',
                                                  square: true,
                                                  icon: DocumentTextIcon,
                                              }),
                                      },
                                  )
                                : null,
                    },
                ),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                href: route(
                                    'front.projectManagement.apikom.updateAppointment',
                                    {
                                        tenant: tenant
                                            ? tenant.id
                                            : props.row.original.tenant.id,
                                        tenantAppointment:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                type: 'primary',
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.apikom.postAppointments'),
    data: {
        tenantId: tenant ? tenant.id : null,
        all,
    },
    columnPinning: {
        right: ['details', 'actions'],
    },
    columnVisibility: {
        'tenant.name': !tenant,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                v-if="tenant"
                size="small"
                :href="
                    route('front.projectManagement.apikom.createAppointment', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
